import { useReducer, useMemo, useEffect, useCallback } from "react";
import { ToastContainer, Zoom } from "react-toastify";

import Header from "./components/Header";

import { AppContextProvider } from "./context/AppContext";

import {
  reducer,
  initialState,
  States,
  Actions,
  HASH_ORDER,
} from "./reducers/Booking";

import useAnalytics from "./hooks/useAnalytics";
import useSessionTimeout from "./hooks/useSessionTimeout";
import useHash from "./hooks/useHash";

import Initial from "./steps/Initial";
import Vehicle from "./steps/Vehicle";
import Dealer from "./steps/Dealer";
import Operations from "./steps/Operations";
import Appointment from "./steps/Appointment";
import Customer from "./steps/Customer";
import Review from "./steps/Review";
import Complete from "./steps/Complete";
import Timeout from "./steps/Timeout";

const SPLIT_SCREEN = [
  States.INITIAL,
  States.VEHICLE,
  States.DEALER,
  States.OPERATIONS,
  States.APPOINTMENT,
  States.CUSTOMER,
];

const SESSION_START_TIMESTAMP_FIELD = "internalStartTimestamp";
const AUTOSAVE_DURATION_MINUTES = 5;
const SESSION_MAX_DURATION_MINUTES = 10;

const stateToHash = (state) =>
  state == null ? "" : States.INITIAL === state ? "" : state.toLowerCase();

const App = () => {
  const [state, dispatch] = useReducer(reducer, {
    ...(() => {
      try {
        if (window.sessionStorage) {
          var obj = JSON.parse(
            window.sessionStorage.getItem("autosave") || "null"
          );
          if (
            typeof obj === "object" &&
            obj.ts &&
            obj.ts > Date.now() &&
            obj.data &&
            obj.data.state
          ) {
            return obj.data;
          }
        }
      } catch {}
      return initialState;
    })(),
    internalStartTimestamp: Date.now(),
  });

  const onHashChange = useCallback(
    (from, to) => {
      const fromIndex = HASH_ORDER.indexOf(from);
      const toIndex = HASH_ORDER.indexOf(to);

      if (fromIndex === -1 || toIndex === -1) {
        return false;
      }

      if (fromIndex <= toIndex) {
        return false;
      }

      if (fromIndex > toIndex) {
        dispatch({
          type: Actions.Back,
          payload: {
            from,
            to,
          },
        });
      }
    },
    [dispatch]
  );

  // eslint-disable-next-line no-unused-vars
  const [hash, pushHash] = useHash(onHashChange);

  useEffect(() => {
    pushHash(stateToHash(state?.state));
    if (window.sessionStorage) {
      window.sessionStorage.setItem(
        "autosave",
        JSON.stringify({
          ts:
            state.internalStartTimestamp +
            AUTOSAVE_DURATION_MINUTES * 60 * 1000,
          data: state,
        })
      );
    }
  }, [state, pushHash]);

  const props = useMemo(
    () => ({ state, dispatch, pushHash }),
    [state, dispatch, pushHash]
  );

  const { onError, createSession } = useAnalytics(state);

  useSessionTimeout(
    state,
    dispatch,
    SESSION_START_TIMESTAMP_FIELD,
    SESSION_MAX_DURATION_MINUTES
  );

  return (
    <>
      <AppContextProvider>
        <>
          <Header {...props} />

          <div className="md:container md:m-auto">
            <div
              className={`flex ${
                state.state === States.INITIAL || state.state === States.VEHICLE
                  ? "flex-col gap-8"
                  : "flex-col-reverse gap-8"
              } md:flex-row px-4 py-16 w-full max-w-full`}
            >
              {SPLIT_SCREEN.includes(state.state) && (
                <div className="flex-none basis-full md:basis-8/12">
                  {state.state === States.INITIAL && (
                    <Initial.Page
                      {...props}
                      onError={onError}
                      createSession={createSession}
                    />
                  )}
                  {state.state === States.VEHICLE && (
                    <Vehicle.Page {...props} onError={onError} />
                  )}
                  {state.state === States.DEALER && <Dealer.Page {...props} />}
                  {state.state === States.OPERATIONS && (
                    <Operations.Page {...props} onError={onError} />
                  )}
                  {state.state === States.APPOINTMENT && (
                    <Appointment.Page {...props} onError={onError} />
                  )}
                  {state.state === States.CUSTOMER && (
                    <Customer.Page {...props} onError={onError} />
                  )}
                </div>
              )}
              {SPLIT_SCREEN.includes(state.state) && (
                <div className="flex-none basis-full md:basis-4/12 md:pl-8 mb-8">
                  {state.state === States.INITIAL && (
                    <Initial.Description state={state} />
                  )}
                  {state.state === States.VEHICLE && (
                    <Vehicle.Description state={state} />
                  )}
                  {state.state === States.DEALER && (
                    <Dealer.Description state={state} />
                  )}
                  {state.state === States.OPERATIONS && (
                    <Operations.Description state={state} />
                  )}
                  {state.state === States.APPOINTMENT && (
                    <Appointment.Description state={state} />
                  )}
                  {state.state === States.CUSTOMER && (
                    <Customer.Description state={state} />
                  )}
                </div>
              )}

              {!SPLIT_SCREEN.includes(state.state) && (
                <div className="flex-none basis-full md:m-auto md:basis-8/12">
                  {state.state === States.REVIEW && (
                    <Review.Page {...props} onError={onError} />
                  )}
                  {state.state === States.COMPLETE && (
                    <Complete.Page {...props} onError={onError} />
                  )}
                  {state.state === States.TIMEOUT && (
                    <Timeout.Page {...props} />
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      </AppContextProvider>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        theme="light"
        transition={Zoom}
      />
    </>
  );
};

export default App;
